import { useLiveQuery } from 'dexie-react-hooks';
import { Camera, Mic, ScanLine, Minus, PauseCircle, Percent, Plus, Printer, ScanBarcode, Search, Trash2, UserRound, Wallet, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GS, LOYALTY_ENABLED, formatMoney, formatQty, lineNet, parseMoney, parseQty, type Product } from '@sense/shared';
import { CustomerPicker } from '../components/CustomerPicker';
import { ManagerApproval } from '../components/ManagerApproval';
import { VoiceBilling, voiceSupported } from '../components/VoiceBilling';
import { ChargeBar, CustomerCard as PhoneCustomerCard, LineCard, LineOptions, MobileBillHeader, SummaryCard } from '../components/MobileBill';
import { PaySheet } from '../components/PaySheet';
import { Scanner } from '../components/Scanner';
import { SyncPill } from '../components/SyncPill';
import { isMeasured, WeightDialog } from '../components/WeightDialog';
import { Button, Kbd, Sheet } from '../components/ui';
import { useUi } from '../components/ui-store';
import { useCart, type CartLine } from '../lib/cart';
import { localeOf, useLang, useT, tr } from '../lib/i18n';
import { currentCashier, useRole } from '../lib/cashier';
import { useCustomer } from '../lib/customers';
import { useProductIndex, search, useProducts, useStockMap, type Lot } from '../lib/catalog';
import { db, peekCounter } from '../lib/db';
import { isTyping, useHotkeys } from '../lib/hotkeys';
import { usePricedCart, type Priced } from '../lib/pricing';
import { reprintLast } from '../lib/reprint';
import { useSession } from '../lib/session';
import { useOpenShift } from '../lib/shift';
import { billNoFor } from '../lib/ops';
import { useWide } from '../lib/useWide';
import { haptic, toast } from '../lib/toast';

type Dialog = null | 'qty' | 'disc' | 'customer' | 'billDisc' | 'held' | 'price' | 'line';

/**
 * Register-style billing: scan/search on top (matches drop down), the bill items are the page,
 * customer and amount details sit on the right (bottom bar on phones).
 */
export function Billing() {
  const tx = useT();
  const products = useProducts();
  const stock = useStockMap();
  const index = useProductIndex();
  const cart = useCart();
  const priced = usePricedCart();
  const totals = priced.totals;
  const navigate = useNavigate();
  const openUi = useUi((s) => s.set);
  const uiBusy = useUi((s) => s.palette || s.help);

  const [query, setQuery] = useState('');
  const [hi, setHi] = useState(0);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [paying, setPaying] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [voice, setVoice] = useState(false);
  const [weighing, setWeighing] = useState<{ product: Product; line?: number } | null>(null);
  const input = useRef<HTMLInputElement>(null);
  // Computers keep the cursor in search. Touch screens don't: focusing would pop the keyboard up on
  // every visit and after every tap (typed / scanned keys still reach search through the key handler).
  const focusSearch = useCallback(() => { if (finePointer()) setTimeout(() => input.current?.focus(), 0); }, []);

  const result = useMemo(() => search(index, query, 8), [index, query]);
  useEffect(() => setHi(0), [query]);
  useEffect(() => { focusSearch(); }, [focusSearch]);

  const add = (p: Product, qty?: number, lot?: Lot) => {
    // Loose items (kg, g, l, ml) need a weight unless a scale barcode or "1.25*" already gave one.
    if (qty == null && isMeasured(p.unit)) { setQuery(''); setWeighing({ product: p }); return; }
    if (lot?.expiry && lot.expiry < Date.now()) toast.error(tr('{0} is past its expiry date ({1}). Check before selling.', { 0: p.name, 1: new Date(lot.expiry).toLocaleDateString('en-IN') }));
    cart.add(p, qty, lot);
    haptic();
    setQuery('');
    if (finePointer()) focusSearch();
    else input.current?.blur(); // touch: close the keyboard so the bill shows
  };

  const submitSearch = () => {
    const text = query.trim();
    if (!text) return;
    const exact = result.matches.find((m) => m.exact);
    if (exact) return add(exact.product, exact.qty ?? result.qty, exact.lot);
    const pick = result.matches[hi];
    if (pick && !/^\d{6,}$/.test(text.replace(/^.*\*/, ''))) return add(pick.product, result.qty);
    if (/^\d{6,14}$/.test(text)) notFoundToast(text, navigate);
    else toast.error(tr('“{0}” not found.', { 0: text }));
    setQuery('');
  };

  const sel = cart.selected >= 0 ? cart.lines[cart.selected] : undefined;
  const holdBill = async () => {
    if (!cart.lines.length) return toast.info(tx('Bill is empty.'));
    await db.held.add({ ts: Date.now(), label: cart.customer?.name ?? `${cart.lines.length} items · ${formatMoney(totals.total)}`, lines: cart.lines.map(({ pid, qty, price, disc, category }) => ({ pid, qty, price, disc, category })), customer: cart.customer, billDiscount: cart.billDiscount });
    cart.clear();
    toast.ok(tx('Bill on hold. Shift F8 to recall.'));
    focusSearch();
  };
  const openPay = () => { if (cart.lines.length) setPaying(true); else toast.info(tx('Add items first.')); };
  // Overrides beyond what a cashier may do wait for a manager's PIN.
  const me = useSession((s) => s.me);
  const role = useRole();
  const boss = role === 'owner' || role === 'manager';
  const [approval, setApproval] = useState<{ reason: string; action: string; run(): void } | null>(null);
  const guarded = (action: string, reason: string, allowed: boolean, run: () => void) => {
    if (allowed || boss) return run();
    setApproval({ reason, action, run });
  };
  const maxPct = me?.store.settings.maxDiscountPct ?? 10;
  const modal = dialog !== null || approval !== null || voice || paying || scanning || weighing !== null || uiBusy;
  /** F2 / tapping the quantity: weighed lines get the weight dialog, others the number dialog. */
  const editQty = (i: number) => {
    const l = cart.lines[i];
    if (!l) return;
    cart.select(i);
    const p = products?.find((x) => x.id === l.pid);
    if (p && isMeasured(l.unit)) setWeighing({ product: { ...p, price: l.price }, line: i });
    else setDialog('qty');
  };

  useHotkeys({
    '!f1': focusSearch,
    '/': focusSearch,
    // F2: type the new quantity / weight straight into the selected line (desktop table), else the dialog.
    '!f2': () => {
      if (!sel) return;
      const el = document.querySelector<HTMLInputElement>(`[data-qty-input="${cart.selected}"]`);
      if (el && el.offsetParent) { el.focus(); el.select(); } else editQty(cart.selected);
    },
    '!f3': () => sel && setDialog('disc'),
    '!f7': () => sel && setDialog('price'),
    '!f4': () => setDialog('customer'),
    '!f6': () => cart.lines.length && setDialog('billDisc'),
    '!f8': () => void holdBill(),
    '!shift+f8': () => setDialog('held'),
    '!f9': () => void reprintLast(),
    '!f10': openPay,
    '!mod+enter': openPay,
    '!alt+arrowup': () => cart.select(Math.max(0, cart.selected - 1)),
    '!alt+arrowdown': () => cart.select(Math.min(cart.lines.length - 1, cart.selected + 1)),
    delete: () => sel && cart.remove(cart.selected),
    '+': () => sel && cart.step(cart.selected, 1),
    '-': () => sel && cart.step(cart.selected, -1),
  }, !modal);

  // Typing anywhere on the till goes to the search field (scanners included).
  useEffect(() => {
    if (modal) return;
    const onKey = (e: KeyboardEvent) => {
      // Scanners send the GS1 group separator as Ctrl+]; keep it so batch and expiry can be read.
      if (e.ctrlKey && e.key === ']') { e.preventDefault(); setQuery((q) => q + GS); input.current?.focus(); return; }
      if (isTyping(e) || e.ctrlKey || e.metaKey || e.altKey || e.key.length !== 1 || '+-/?'.includes(e.key)) return;
      e.preventDefault();
      setQuery((q) => q + e.key);
      input.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modal]);

  const onSearchKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.ctrlKey && e.key === ']') { e.preventDefault(); setQuery((q) => q + GS); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi((h) => Math.min(h + 1, result.matches.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
      if (query.trim()) submitSearch();
      else if (cart.lines.length) openPay();
    } else if (e.key === 'Escape') { e.preventDefault(); setQuery(''); }
    else if (!query && (e.key === '+' || e.key === '-') && sel) { e.preventDefault(); cart.step(cart.selected, e.key === '+' ? 1 : -1); }
  };

  const desk = useWide();
  const dropdown = query.trim() !== '' && (
    <div className="absolute inset-x-3 top-full z-30 -mt-1 max-h-[55dvh] overflow-y-auto rounded-2xl border border-line bg-white text-ink shadow-lift lg:inset-x-0 lg:mt-2" role="listbox" aria-label="Matching products">
      {result.qty != null && <div className="border-b border-line bg-forest-50 px-4 py-2 text-sm font-bold text-primary">{tr('Adding ×{0} — pick an item', { 0: formatQty(result.qty, 'pc') })}</div>}
      {result.matches.length === 0 && <div className="px-4 py-4 text-sm text-muted">{tr('No product matches “{0}”. Press', { 0: query })} <Kbd>{tr('Esc')}</Kbd> {tr('to clear.')}</div>}
      {result.matches.map((m, i) => {
        const p = m.product;
        const s = stock.get(p.id);
        const low = s != null && (s <= 0 || (p.reorder != null && s <= p.reorder));
        return (
          <button key={p.id} role="option" aria-selected={i === hi} onMouseEnter={() => setHi(i)} onMouseDown={(e) => e.preventDefault()} onClick={() => add(p, m.qty ?? result.qty)}
            className={`flex w-full items-center gap-3 px-4 py-3 text-left ${i === hi ? 'bg-surface-2' : ''}`}>
            <span className="min-w-0 flex-1">
              <span className="line-clamp-2 block font-semibold lg:truncate">{p.name}</span>
              <span className="num block truncate text-xs text-muted">{p.sku}{p.barcodes[0] ? ` · ${p.barcodes[0]}` : ''} · {p.category}</span>
            </span>
            <span className={`num hidden text-xs font-semibold sm:inline ${low ? 'text-danger' : 'text-muted'}`}>{s == null ? '' : `${formatQty(s, p.unit)} left`}</span>
            <span className="num shrink-0 text-right font-semibold lg:w-24">{formatMoney(p.price)}{p.unit !== 'pc' && p.unit !== 'pack' ? <span className="text-xs text-muted">/{p.unit}</span> : null}</span>
          </button>
        );
      })}
    </div>
  );

  const searchBox = (
    <div className="relative min-w-0 flex-1">
      <label className="flex h-12 items-center gap-2 rounded-full border border-line bg-white pr-1.5 pl-4 focus-within:border-forest-600 lg:h-14 lg:border-2 lg:border-forest-600 lg:pr-4">
        <ScanBarcode size={22} className="hidden shrink-0 text-primary lg:block" aria-hidden />
        <Search size={20} className="shrink-0 text-muted lg:hidden" aria-hidden />
        <span className="sr-only">{tr('Scan or search products')}</span>
        <input ref={input} value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={onSearchKey} enterKeyHint="search"
          placeholder={window.innerWidth < 1024 ? tx('Scan or search') : tx('Scan barcode or type item name / code   (3*milk adds 3)')} autoComplete="off" spellCheck={false}
          className="min-w-0 flex-1 bg-transparent text-base outline-none lg:text-lg" />
        {query ? <button aria-label="Clear search" onClick={() => { setQuery(''); focusSearch(); }} className="text-muted"><X size={18} /></button> : <span className="hidden lg:inline"><Kbd>F1</Kbd></span>}
        {!query && voiceSupported() && <span className="w-8 shrink-0 lg:hidden" aria-hidden />}
      </label>
      {/* Phone: the microphone sits inside the search field (outside the label, so it keeps its own name). */}
      {!query && voiceSupported() && (
        <button type="button" aria-label="Bill by voice" onClick={() => setVoice(true)} className="absolute top-1.5 right-1.5 flex h-9 w-9 items-center justify-center rounded-full text-ink lg:hidden"><Mic size={20} /></button>
      )}
      {desk && dropdown}
    </div>
  );

  const lineProps = (i: number) => ({
    onSelect: () => cart.select(i), onDec: () => cart.step(i, -1), onInc: () => cart.step(i, 1),
    onQty: () => editQty(i), onSetQty: (q: number) => { cart.setQty(i, q); focusSearch(); }, onDone: focusSearch, onDisc: () => { cart.select(i); setDialog('disc'); }, onPrice: () => { cart.select(i); setDialog('price'); }, onRemove: () => cart.remove(i),
  });

  return (
    <div className="flex h-app flex-col lg:flex-row">
      {/* ---------- items ---------- */}
      <section className="flex min-h-0 min-w-0 flex-1 flex-col">
        <MobileBillHeader onMenu={() => openUi({ drawer: true })} onHold={() => void holdBill()} onHeld={() => setDialog('held')} count={cart.lines.length}
          onClear={() => { if (confirm(tx('Clear this bill?'))) { cart.clear(); focusSearch(); } }} />
        <div className="relative flex items-center gap-2 px-4 pt-1 pb-2 lg:px-6 lg:pt-5 lg:pb-3">
          {searchBox}
          <button aria-label="Scan with camera" onClick={() => setScanning(true)} className="flex h-12 shrink-0 items-center gap-2 rounded-full bg-forest-700 px-4 font-bold text-white lg:h-14 lg:w-14 lg:justify-center lg:border lg:border-line lg:bg-white lg:px-0 lg:text-primary">
            <ScanLine size={21} className="lg:hidden" /><span className="lg:hidden">{tx('Scan')}</span><Camera size={22} className="hidden lg:block" />
          </button>
          {voiceSupported() && (
            <button aria-label="Bill by voice" title={tr('Bill by voice (English / தமிழ் / हिन्दी)')} onClick={() => setVoice(true)} className="hidden h-14 w-14 shrink-0 items-center justify-center rounded-full border border-line bg-white text-primary lg:flex">
              <Mic size={22} />
            </button>
          )}
          <span className="hidden xl:block"><SyncPill /></span>
          {/* Phones: results span the whole row (search and Scan), so names fit on one line. */}
          {!desk && dropdown}
        </div>

        {/* phone: customer */}
        <div className="px-4 pt-1 pb-1 lg:hidden">
          <PhoneCustomerCard customer={cart.customer} onPick={() => setDialog('customer')} onClear={() => cart.setCustomer(null)} />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 pt-2 pb-[calc(var(--tabbar-h,64px)+5.5rem)] lg:px-6 lg:pb-6">
          {!cart.lines.length ? (
            <EmptyBill loading={products === undefined} noProducts={products?.length === 0} onAddProduct={() => navigate('/inventory?new=1')} />
          ) : (
            <>
              <BillTable priced={priced} lineProps={lineProps} />
              <div className="flex flex-col gap-2 lg:hidden">
                {cart.lines.map((l, i) => (
                  <LineCard key={i} l={l} offer={priced.autoLine[i] ?? 0} offerName={priced.lineOfferName[i]}
                    onDec={() => cart.step(i, -1)} onInc={() => cart.step(i, 1)} onQty={() => editQty(i)}
                    onPrice={() => { cart.select(i); setDialog('price'); }} onOptions={() => { cart.select(i); setDialog('line'); }} onRemove={() => cart.remove(i)} />
                ))}
                <div className="mt-2"><SummaryCard priced={priced} onDiscount={() => setDialog('billDisc')} /></div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* ---------- amount details (desktop) ---------- */}
      <SummaryPanel priced={priced} onCustomer={() => setDialog('customer')} onClearCustomer={() => cart.setCustomer(null)}
        onBillDisc={() => setDialog('billDisc')} onHeld={() => setDialog('held')} onHold={() => void holdBill()} onPay={openPay} />

      {/* ---------- total + charge (phone) ---------- */}
      {cart.lines.length > 0 && <ChargeBar priced={priced} count={cart.lines.length} onCharge={openPay} />}
      <LineOptions line={dialog === 'line' ? sel ?? null : null} onClose={() => setDialog(null)}
        onQty={() => { setDialog(null); editQty(cart.selected); }} onPrice={() => setDialog('price')} onDiscount={() => setDialog('disc')}
        onRemove={() => { cart.remove(cart.selected); setDialog(null); }} />

      {/* ---------- dialogs ---------- */}
      <NumberDialog open={dialog === 'qty'} onClose={() => { setDialog(null); focusSearch(); }}
        title={sel ? tr('Quantity · {0}', { 0: sel.name }) : tr('Quantity')} label={sel?.unit === 'pc' || sel?.unit === 'pack' ? tr('Quantity') : tr('Weight / volume ({0})', { 0: sel?.unit })}
        initial={sel ? String(sel.qty / 1000) : ''}
        onSubmit={(v) => { const q = parseQty(v); if (!Number.isFinite(q)) return 'Enter a number'; cart.setQty(cart.selected, q); }} />
      <NumberDialog open={dialog === 'disc'} onClose={() => { setDialog(null); focusSearch(); }}
        title={sel ? tr('Discount · {0}', { 0: sel.name }) : tr('Discount')} label={tr('Amount in ₹ or percent (e.g. 10%)')} initial={sel?.disc ? String(sel.disc / 100) : ''}
        onSubmit={(v) => {
          if (!sel) return;
          const gross = Math.round((sel.price * sel.qty) / 1000); const d = discountFrom(v, gross); if (d == null) return 'Enter an amount or %';
          const i = cart.selected;
          guarded('discount', `${sel.name}: discount over ${maxPct}% needs a manager.`, d <= (gross * maxPct) / 100, () => cart.setDisc(i, Math.min(d, gross)));
        }} />
      <NumberDialog open={dialog === 'price'} onClose={() => { setDialog(null); focusSearch(); }}
        title={sel ? tr('Price · {0}', { 0: sel.name }) : tr('Price')} label={tr('New price in ₹{0}', { 0: sel && sel.unit !== 'pc' && sel.unit !== 'pack' ? ` per ${sel.unit}` : '' })} initial={sel ? String(sel.price / 100) : ''}
        onSubmit={(v) => {
          if (!sel) return;
          const p = parseMoney(v); if (!Number.isFinite(p) || p < 0) return 'Enter a price';
          const i = cart.selected;
          guarded('price', `Change ${sel.name} from ${formatMoney(sel.price)} to ${formatMoney(p)}.`, !!me?.store.settings.allowPriceOverride, () => cart.setPrice(i, p));
        }} />
      <NumberDialog open={dialog === 'billDisc'} onClose={() => { setDialog(null); focusSearch(); }}
        title={tr('Discount on whole bill')} label={tr('Amount in ₹ or percent (e.g. 5%)')} initial={cart.billDiscount ? String(cart.billDiscount / 100) : ''}
        onSubmit={(v) => {
          const d = discountFrom(v, totals.subtotal); if (d == null) return 'Enter an amount or %';
          guarded('discount', `Bill discount over ${maxPct}% needs a manager.`, d <= (totals.subtotal * maxPct) / 100, () => cart.setBillDiscount(d));
        }} />
      <ManagerApproval open={approval !== null} reason={approval?.reason ?? ''} onClose={() => { setApproval(null); focusSearch(); }}
        onApproved={(name) => { approval?.run(); cart.approve(approval!.action, name); toast.ok(tr('Approved by {0}', { 0: name })); setApproval(null); focusSearch(); }} />
      <CustomerPicker open={dialog === 'customer'} onClose={() => { setDialog(null); focusSearch(); }}
        onPick={(c) => { cart.setCustomer({ id: c.id, name: c.name, phone: c.phone }); setDialog(null); focusSearch(); }} />
      <HeldDialog open={dialog === 'held'} onClose={() => { setDialog(null); focusSearch(); }} />

      <VoiceBilling open={voice} onClose={() => { setVoice(false); focusSearch(); }} onAdd={(p, q) => cart.add(p, q)} />
      <PaySheet open={paying} onClose={() => { setPaying(false); focusSearch(); }} />
      <Scanner open={scanning} continuous onClose={() => { setScanning(false); focusSearch(); }}
        onCode={(code) => {
          const r = search(index, code);
          const hit = r.matches.find((m) => m.exact);
          if (!hit) return toast.error(tr('Barcode {0} not found', { 0: code }));
          if (hit.qty == null && isMeasured(hit.product.unit)) { setScanning(false); setWeighing({ product: hit.product }); return; }
          if (hit.lot?.expiry && hit.lot.expiry < Date.now()) toast.error(tr('{0} is past its expiry date.', { 0: hit.product.name }));
          cart.add(hit.product, hit.qty, hit.lot); haptic(30);
        }} />
      <WeightDialog product={weighing?.product ?? null} initial={weighing?.line != null ? cart.lines[weighing.line]?.qty : undefined}
        onClose={() => { setWeighing(null); focusSearch(); }}
        onConfirm={(q) => {
          if (weighing?.line != null) cart.setQty(weighing.line, q);
          else if (weighing) cart.add(weighing.product, q);
          haptic();
          setWeighing(null);
          focusSearch();
        }} />
    </div>
  );
}

type LineHandlers = { onSelect(): void; onDec(): void; onInc(): void; onQty(): void; onSetQty(q: number): void; onDone(): void; onDisc(): void; onPrice(): void; onRemove(): void };

function EmptyBill({ loading, noProducts, onAddProduct }: { loading: boolean; noProducts: boolean; onAddProduct(): void }) {
  const tx = useT();
  if (loading) return <div className="p-10 text-center text-muted">{tx('Loading products…')}</div>;
  return (
    <div className="flex h-full min-h-72 flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-line px-6 py-10 text-center lg:bg-white">
      <ScanBarcode size={44} className="text-forest-200" />
      <div className="text-lg font-bold">{tx(noProducts ? 'No products yet' : 'Scan or search to add items')}</div>
      {noProducts ? (
        <Button onClick={onAddProduct}>{tx('Add product')}</Button>
      ) : (
        <div className="hidden max-w-md flex-wrap justify-center gap-x-4 gap-y-2 text-sm text-muted lg:flex">
          <span><Kbd>{tr('3*milk')}</Kbd> {tr('adds 3')}</span><span><Kbd>F4</Kbd> {tr('customer')}</span><span><Kbd>F8</Kbd> {tr('hold')}</span><span><Kbd>F10</Kbd> {tr('pay')}</span><span><Kbd>?</Kbd> {tr('all shortcuts')}</span>
        </div>
      )}
    </div>
  );
}

function BillTable({ priced, lineProps }: { priced: Priced; lineProps(i: number): LineHandlers }) {
  const { lines, selected } = useCart();
  const ref = useRef<HTMLTableSectionElement>(null);
  useEffect(() => { ref.current?.querySelector('[data-selected="true"]')?.scrollIntoView({ block: 'nearest' }); }, [selected, lines.length]);
  return (
    <div className="hidden overflow-hidden rounded-2xl border border-line bg-white lg:block">
      <table className="w-full text-sm">
        <thead className="bg-forest-50 text-left text-xs font-bold text-primary">
          <tr>
            <th className="w-10 py-3 pl-4">#</th><th className="py-3">{tr('Item')}</th><th className="w-40 text-center">{tr('Qty')}</th>
            <th className="w-24 text-right">{tr('Rate')}</th><th className="w-28 text-right">{tr('Discount')}</th><th className="w-28 pr-3 text-right">{tr('Amount')}</th><th className="w-20" />
          </tr>
        </thead>
        <tbody ref={ref}>
          {lines.map((l, i) => {
            const h = lineProps(i);
            const offer = priced.autoLine[i] ?? 0;
            const isSel = i === selected;
            return (
              <tr key={i} data-selected={isSel} onClick={h.onSelect} className={`cursor-default border-t border-line ${isSel ? 'bg-forest-50' : 'hover:bg-paper'}`}>
                <td className="num py-2.5 pl-4 text-muted">{i + 1}</td>
                <td className="py-2.5">
                  <div className="font-bold">{l.name}</div>
                  {offer > 0 && <div className="mt-0.5 flex items-center gap-1.5 text-xs font-bold text-t-amber-fg"><span className="rounded-full bg-amber px-1.5 py-px text-[10px] text-white">OFFER</span>{priced.lineOfferName[i]}</div>}
                  <LotChip l={l} />
                </td>
                <td>
                  <div className="flex items-center justify-center gap-1">
                    <button aria-label={`Decrease ${l.name}`} onClick={(e) => { e.stopPropagation(); h.onDec(); }} className="flex h-8 w-8 items-center justify-center rounded-full border border-line bg-white text-primary"><Minus size={15} /></button>
                    <QtyInput line={l} index={i} onCommit={h.onSetQty} onCancel={h.onDone} />
                    <button aria-label={`Increase ${l.name}`} onClick={(e) => { e.stopPropagation(); h.onInc(); }} className="flex h-8 w-8 items-center justify-center rounded-full border border-line bg-white text-primary"><Plus size={15} /></button>
                  </div>
                </td>
                <td className="num text-right"><button title={tr('Change price (F7)')} onClick={(e) => { e.stopPropagation(); h.onPrice(); }} className="rounded-lg px-1.5 py-0.5 hover:bg-white">{formatMoney(l.price)}</button></td>
                <td className="num text-right">
                  {l.disc + offer > 0 ? <span className={offer ? 'text-t-amber-fg' : 'text-primary'}>−{formatMoney(l.disc + offer)}</span> : <span className="text-muted">—</span>}
                </td>
                <td className="num pr-3 text-right font-semibold">{formatMoney(lineNet([l.pid, l.qty, l.price, l.disc + offer, l.tax_bp]))}</td>
                <td className="pr-2">
                  <div className={`flex justify-end gap-0.5 ${isSel ? '' : 'opacity-0 hover:opacity-100'}`}>
                    <button aria-label={`Discount on ${l.name}`} title={tr('Discount (F3)')} onClick={(e) => { e.stopPropagation(); h.onDisc(); }} className="flex h-8 w-8 items-center justify-center rounded-full text-primary hover:bg-white"><Percent size={15} /></button>
                    <button aria-label={`Remove ${l.name}`} title={tr('Remove (Del)')} onClick={(e) => { e.stopPropagation(); h.onRemove(); }} className="flex h-8 w-8 items-center justify-center rounded-full text-danger hover:bg-white"><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CustomerCard({ c, onChange, onClear }: { c: { id: string; name: string; phone: string }; onChange(): void; onClear(): void }) {
  const full = useCustomer(c.id);
  return (
    <div className="flex items-center gap-3 rounded-3xl bg-forest-50 p-3">
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-forest-700 font-bold text-white">{c.name.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase()}</span>
      <button onClick={onChange} className="min-w-0 flex-1 text-left">
        <span className="block truncate font-bold">{c.name}</span>
        <span className="num block text-xs text-muted">{c.phone}</span>
        <span className="mt-1 flex flex-wrap gap-1.5">
          {LOYALTY_ENABLED && <span className="num rounded-full bg-t-violet px-2 py-0.5 text-2xs font-semibold text-t-violet-fg">{tr('{0} pts', { 0: full?.points ?? 0 })}</span>}
          {(full?.balance ?? 0) > 0 && <span className="num rounded-full bg-t-red px-2 py-0.5 text-2xs font-semibold text-danger">{tr('khata {0}', { 0: formatMoney(full!.balance, { whole: true }) })}</span>}
          {(full?.visits ?? 0) > 0 && <span className="rounded-full bg-white px-2 py-0.5 text-2xs font-semibold text-muted">{tr('{0} visits', { 0: full!.visits })}</span>}
        </span>
      </button>
      <button aria-label="Remove customer" onClick={onClear} className="flex h-9 w-9 items-center justify-center rounded-full text-muted hover:bg-white"><X size={18} /></button>
    </div>
  );
}

function HeldButton({ onClick, wide = false }: { onClick(): void; wide?: boolean }) {
  const held = useLiveQuery(() => db.held.count(), []);
  return (
    <Button variant="outline" size="sm" onClick={onClick} title={tr('Recall held bill (Shift F8)')} className={wide ? 'flex-1' : ''}>
      <PauseCircle size={15} className="shrink-0" /><span className="truncate">{tr('Held')}{held ? <span className="ml-1 rounded-full bg-forest-700 px-1.5 text-2xs text-white">{held}</span> : null}</span>
    </Button>
  );
}

function SummaryPanel({ priced, onCustomer, onClearCustomer, onBillDisc, onHeld, onHold, onPay }: {
  priced: Priced; onCustomer(): void; onClearCustomer(): void; onBillDisc(): void; onHeld(): void; onHold(): void; onPay(): void;
}) {
  const { lines, customer } = useCart();
  const me = useSession((s) => s.me);
  const devNo = useLiveQuery(() => peekCounter('devNo'), [lines.length]);
  const [clock, setClock] = useState(() => new Date());
  useEffect(() => { const t = setInterval(() => setClock(new Date()), 30_000); return () => clearInterval(t); }, []);
  const { totals } = priced;
  const units = lines.reduce((s, l) => s + (l.unit === 'pc' || l.unit === 'pack' ? l.qty / 1000 : 1), 0);
  const saved = priced.applied.reduce((a, o) => a + o.amount, 0);
  const empty = lines.length === 0;
  const shift = useOpenShift();
  const tx = useT();
  const locale = localeOf(useLang((x) => x.lang));
  return (
    <aside aria-label="Bill summary" className="hidden w-[380px] shrink-0 flex-col border-l border-line bg-white lg:flex xl:w-[400px]">
      <div className="flex items-start justify-between gap-2 border-b border-line px-5 pt-5 pb-4">
        <div>
          <div className="text-xs font-bold text-muted uppercase">{tx('Bill no.')}</div>
          <div className="num text-lg font-semibold">{billNoFor((devNo ?? 0) + 1)}</div>
        </div>
        <div className="text-right text-xs text-muted">
          <div>{clock.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })} · {clock.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}</div>
          <div className="mt-0.5">{tx('Cashier')} <b className="text-ink">{currentCashier().name}</b></div>
        </div>
      </div>
      {shift === null && (
        <Link to="/day-close" className="mx-5 mt-3 flex items-center gap-2 rounded-full bg-t-amber px-3 py-1.5 text-xs font-bold text-t-amber-fg hover:brightness-95">
          <Wallet size={14} />{tx('No shift open · count the cash drawer')}<span className="ml-auto underline">{tx('Open shift')}</span>
        </Link>
      )}

      <div className="border-b border-line px-5 py-4">
        {customer ? <CustomerCard c={customer} onChange={onCustomer} onClear={onClearCustomer} /> : (
          <button onClick={onCustomer} className="flex w-full items-center gap-3 rounded-3xl border-2 border-dashed border-line p-3 text-left hover:bg-forest-50">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-t-green text-primary"><UserRound size={20} /></span>
            <span className="flex-1"><span className="block font-bold text-primary">{tx('Add customer')}</span><span className="text-xs text-muted">{tx('Khata and e-receipt')}</span></span>
            <Kbd>F4</Kbd>
          </button>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-2.5 overflow-y-auto px-5 py-4 text-base">
        <div className="flex justify-between"><span className="text-muted">{tx('Items / Qty')}</span><span className="num">{lines.length} / {Number.isInteger(units) ? units : units.toFixed(2)}</span></div>
        <div className="flex justify-between"><span className="text-muted">{tx('Subtotal')}</span><span className="num">{formatMoney(totals.subtotal)}</span></div>
        {priced.applied.map((a) => (
          <div key={a.offerId} className="flex justify-between gap-3 font-bold text-t-amber-fg"><span className="truncate">{a.name}</span><span className="num">−{formatMoney(a.amount)}</span></div>
        ))}
        <div className="flex items-center justify-between">
          <button onClick={onBillDisc} disabled={empty} className="flex items-center gap-1.5 text-muted hover:text-primary disabled:hover:text-muted">{tx('Discount')} <Kbd>F6</Kbd></button>
          <span className="num">{priced.manual ? `−${formatMoney(priced.manual)}` : formatMoney(0)}</span>
        </div>
        <div className="flex justify-between"><span className="text-muted">{tx('GST (included)')}</span><span className="num">{formatMoney(totals.tax)}</span></div>
        {totals.round !== 0 && <div className="flex justify-between"><span className="text-muted">{tx('Round off')}</span><span className="num">{totals.round > 0 ? '+' : ''}{formatMoney(totals.round)}</span></div>}
        {saved > 0 && <div className="rounded-xl bg-t-amber px-3 py-2 text-center text-sm font-bold text-t-amber-fg">{tx('Customer saves {a}', { a: formatMoney(saved) })}</div>}
      </div>

      <div className="flex flex-col gap-3 border-t border-line bg-forest-50 px-5 pt-4 pb-5">
        <div className="flex items-baseline justify-between">
          <span className="text-sm font-bold tracking-wide text-primary uppercase">{tx('Total')}</span>
          <span className="num text-3xl font-bold">{formatMoney(totals.total)}</span>
        </div>
        <Button size="lg" onClick={onPay} disabled={empty} className="h-16 text-lg">{tx('Pay')} {empty ? '' : formatMoney(totals.total)} <Kbd>F10</Kbd></Button>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="flex-1" onClick={onHold} disabled={empty}>{tx('Hold')} <Kbd>F8</Kbd></Button>
          <HeldButton onClick={onHeld} wide />
          <Button variant="outline" size="sm" className="flex-1" onClick={() => void reprintLast()} title={tr('Reprint last bill (F9)')}><Printer size={16} />{tx('Reprint')}</Button>
        </div>
      </div>
    </aside>
  );
}

function notFoundToast(code: string, navigate: (to: string) => void) {
  toast.ok(tr('Barcode {0} isn’t in your catalog.', { 0: code }), { label: tr('Add product'), run: () => navigate(`/inventory?new=1&barcode=${code}`) });
}

function discountFrom(v: string, base: number): number | null {
  const t = v.trim();
  if (!t) return 0;
  if (t.endsWith('%')) {
    const pct = parseFloat(t);
    return Number.isFinite(pct) && pct >= 0 && pct <= 100 ? Math.round((base * pct) / 100) : null;
  }
  const paise = parseMoney(t);
  return Number.isFinite(paise) && paise >= 0 ? paise : null;
}

function LineRow({ l, offer, offerName, selected, touch, onSelect, onDec, onInc, onQty, onDisc, onPrice, onRemove }: { l: CartLine; offer: number; offerName?: string; selected: boolean; touch: boolean; onSelect(): void; onDec(): void; onInc(): void; onQty(): void; onDisc(): void; onPrice?(): void; onRemove(): void }) {
  const b = touch ? 'h-11 w-11' : 'h-8 w-8';
  return (
    <div data-selected={selected} onClick={onSelect} className={`flex flex-col gap-1.5 border-b border-line px-4 py-2.5 ${selected ? 'bg-forest-50' : ''}`}>
      <div className="flex justify-between gap-3">
        <span className="min-w-0 text-sm font-bold">{l.name}
          {/* Very narrow phones: the price sits under the name instead of squeezed beside the buttons. */}
          <button onClick={(e) => { e.stopPropagation(); onPrice?.(); }} className="num block text-left text-xs font-normal text-muted min-[360px]:hidden" title={tr('Change price (F7)')}>@ {formatMoney(l.price)}{l.disc ? ` · −${formatMoney(l.disc)}` : ''}</button>
        </span>
        <span className="num text-sm font-semibold">{formatMoney(lineNet([l.pid, l.qty, l.price, l.disc + offer, l.tax_bp]))}</span>
      </div>
      <LotChip l={l} />
      {offer > 0 && (
        <div className="flex items-center gap-2 text-xs font-bold text-t-amber-fg">
          <span className="rounded-full bg-amber px-2 py-0.5 text-white">{tr('Offer')}</span>{offerName}<span className="num ml-auto">−{formatMoney(offer)}</span>
        </div>
      )}
      <div className="flex items-center gap-1.5">
        <button aria-label="Decrease" onClick={(e) => { e.stopPropagation(); onDec(); }} className={`${b} flex items-center justify-center rounded-full border border-line bg-white text-primary`}><Minus size={16} /></button>
        <button onClick={(e) => { e.stopPropagation(); onQty(); }} className="num min-w-16 rounded-full px-1 text-center text-sm hover:bg-white" title={tr('Change quantity (F2)')}>{formatQty(l.qty, l.unit)}</button>
        <button aria-label="Increase" onClick={(e) => { e.stopPropagation(); onInc(); }} className={`${b} flex items-center justify-center rounded-full border border-line bg-white text-primary`}><Plus size={16} /></button>
        <button onClick={(e) => { e.stopPropagation(); onPrice?.(); }} className="num flex-1 truncate text-left text-xs text-muted max-[359px]:invisible" title={tr('Change price (F7)')}>@ {formatMoney(l.price)}{l.disc ? ` · −${formatMoney(l.disc)}` : ''}</button>
        {(selected || touch) && (
          <>
            <button aria-label="Line discount" title={tr('Discount (F3)')} onClick={(e) => { e.stopPropagation(); onDisc(); }} className={`${b} flex items-center justify-center rounded-full text-primary hover:bg-white`}><Percent size={16} /></button>
            <button aria-label="Remove line" title={tr('Remove (Del)')} onClick={(e) => { e.stopPropagation(); onRemove(); }} className={`${b} flex items-center justify-center rounded-full text-danger hover:bg-white`}><Trash2 size={16} /></button>
          </>
        )}
      </div>
    </div>
  );
}

function Totals({ priced }: { priced: Priced }) {
  const { totals } = priced;
  const offers = priced.autoLine.reduce((a, b) => a + b, 0) + priced.autoBill;
  return (
    <div className="flex flex-col gap-1 text-sm">
      <div className="flex justify-between"><span className="text-muted">{tr('Subtotal')}</span><span className="num">{formatMoney(totals.subtotal)}</span></div>
      {priced.applied.map((a) => (
        <div key={a.offerId} className="flex justify-between font-bold text-t-amber-fg"><span>{a.name}</span><span className="num">−{formatMoney(a.amount)}</span></div>
      ))}
      {priced.manual > 0 && <div className="flex justify-between"><span className="text-muted">{tr('Discount')}</span><span className="num text-primary">−{formatMoney(priced.manual)}</span></div>}
      {totals.round !== 0 && <div className="flex justify-between"><span className="text-muted">{tr('Round off')}</span><span className="num">{totals.round > 0 ? '+' : ''}{formatMoney(totals.round)}</span></div>}
      <div className="flex justify-between"><span className="text-muted">{tr('GST included')}</span><span className="num">{formatMoney(totals.tax)}</span></div>
      {offers > 0 && <div className="sr-only">{tr('Offers save {0}', { 0: formatMoney(offers) })}</div>}
    </div>
  );
}

function NumberDialog({ open, onClose, title, label, initial, onSubmit, inputMode = 'decimal', allowEmpty = false }: {
  open: boolean; onClose(): void; title: string; label: string; initial: string; onSubmit(v: string): string | void; inputMode?: 'decimal' | 'tel'; allowEmpty?: boolean;
}) {
  const [v, setV] = useState(initial);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { if (open) { setV(initial); setErr(null); } }, [open, initial]);
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!v.trim() && !allowEmpty) return onClose();
    const r = onSubmit(v);
    if (typeof r === 'string') setErr(r); else onClose();
  };
  return (
    <Sheet open={open} onClose={onClose} title={title}>
      <form onSubmit={submit} className="flex flex-col gap-3 pb-2">
        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-bold text-muted">{label}</span>
          <input autoFocus inputMode={inputMode} value={v} onChange={(e) => setV(e.target.value)} onFocus={(e) => e.target.select()}
            className="num h-14 rounded-xl border border-line px-4 text-2xl outline-none focus:border-forest-600" />
        </label>
        {err && <div className="text-sm font-bold text-danger">{err}</div>}
        <Button size="lg">{tr('Apply')} <span className="text-xs opacity-80">{tr('Enter')}</span></Button>
      </form>
    </Sheet>
  );
}

function HeldDialog({ open, onClose }: { open: boolean; onClose(): void }) {
  const held = useLiveQuery(() => db.held.orderBy('ts').reverse().toArray(), []);
  const cart = useCart();
  const recall = async (id: number) => {
    const h = await db.held.get(id);
    if (!h) return;
    const products = await db.products.bulkGet(h.lines.map((l) => l.pid));
    if (cart.lines.length) {
      await db.held.add({ ts: Date.now(), label: `${cart.lines.length} items (swapped)`, lines: cart.lines.map(({ pid, qty, price, disc, category }) => ({ pid, qty, price, disc, category })), customer: cart.customer, billDiscount: cart.billDiscount });
    }
    cart.load(h.lines.map((l, i) => ({ ...l, name: products[i]?.name ?? `#${l.pid}`, unit: products[i]?.unit ?? 'pc', tax_bp: products[i]?.tax_bp ?? 0, category: products[i]?.category ?? l.category })), typeof h.customer === 'object' ? h.customer : null, h.billDiscount);
    await db.held.delete(id);
    onClose();
  };
  return (
    <Sheet open={open} onClose={onClose} title={tr('Held bills')}>
      {!held?.length && <div className="py-8 text-center text-sm text-muted">{tr('No bills on hold. Press')} <Kbd>F8</Kbd> {tr('to park the current bill.')}</div>}
      <ul className="flex flex-col gap-2 pb-2">
        {held?.map((h, i) => (
          <li key={h.id}>
            <button onClick={() => void recall(h.id!)} className="flex w-full items-center gap-3 rounded-xl border border-line px-3 py-3 text-left hover:bg-forest-50">
              <span className="num flex h-8 w-8 items-center justify-center rounded-lg bg-t-amber text-sm font-semibold text-t-amber-fg">{i + 1}</span>
              <span className="flex-1"><span className="block font-bold">{h.label}</span><span className="text-xs text-muted">{new Date(h.ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span></span>
              <span className="text-sm font-bold text-primary">{tr('Recall')}</span>
            </button>
          </li>
        ))}
      </ul>
    </Sheet>
  );
}

/** Batch / expiry read from a GS1 label; expired stock is flagged in red. */
function LotChip({ l }: { l: CartLine }) {
  if (!l.batch && !l.expiry) return null;
  const expired = l.expiry != null && l.expiry < Date.now();
  return (
    <div className="mt-0.5 flex flex-wrap gap-1.5 text-2xs font-bold">
      {l.expiry != null && <span className={`rounded-full px-2 py-px ${expired ? 'bg-t-red text-danger' : 'bg-t-teal text-t-teal-fg'}`}>{expired ? 'EXPIRED ' : tr('Exp ')}{new Date(l.expiry).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' })}</span>}
      {l.batch && <span className="rounded-full bg-paper px-2 py-px text-muted">{tr('Batch {0}', { 0: l.batch })}</span>}
    </div>
  );
}

/** Quantity cell you can type into: kg / l with decimals (1.25), g / ml and pieces as whole numbers. */
function QtyInput({ line, index, onCommit, onCancel }: { line: CartLine; index: number; onCommit(qty: number): void; onCancel(): void }) {
  const shown = (q: number) => String(+(q / 1000).toFixed(3));
  const [v, setV] = useState(shown(line.qty));
  const [editing, setEditing] = useState(false);
  useEffect(() => { if (!editing) setV(shown(line.qty)); }, [line.qty, editing]);
  const decimals = line.unit === 'kg' || line.unit === 'l';
  // Read the field itself, not state: a fast Enter can arrive before the last keystroke re-renders.
  const commit = (e: { currentTarget: HTMLInputElement }) => {
    setEditing(false);
    const n = parseFloat(e.currentTarget.value.replace(',', '.'));
    if (!Number.isFinite(n) || n < 0) { setV(shown(line.qty)); return onCancel(); }
    const q = decimals ? Math.round(n * 1000) : Math.round(n) * 1000;
    if (q === line.qty) return onCancel();
    if (q > 100_000_000) { setV(shown(line.qty)); toast.error(tr('That quantity is too large.')); return onCancel(); }
    onCommit(q);
  };
  const unit = line.unit === 'pc' || line.unit === 'pack' ? '' : line.unit;
  return (
    <label className="flex min-w-16 items-center justify-center rounded-full px-1 py-1 focus-within:bg-white focus-within:ring-2 focus-within:ring-forest-600 hover:bg-white" onClick={(e) => e.stopPropagation()} title={tr('Type a new quantity (F2)')}>
      <input data-qty-input={index} aria-label={`Quantity of ${line.name}`} inputMode={decimals ? 'decimal' : 'numeric'} value={v}
        onFocus={(e) => { setEditing(true); e.target.select(); }} onBlur={commit}
        onChange={(e) => setV(decimals ? e.target.value.replace(/[^\d.,]/g, '') : e.target.value.replace(/\D/g, ''))}
        onKeyDown={(e) => {
          if (e.key.length === 1 || e.key === 'Delete' || e.key === 'Backspace') e.stopPropagation(); // +, -, Delete edit this field
          else if (/^F\d+$/.test(e.key)) (e.target as HTMLInputElement).blur(); // F10 pays with the new quantity
          if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
          else if (e.key === 'Escape') { e.preventDefault(); setV(shown(line.qty)); setEditing(false); onCancel(); }
        }}
        className="num w-14 bg-transparent text-center outline-none" style={{ width: `${Math.max(3, v.length + 1)}ch` }} />
      {unit && <span className="num text-xs text-muted">{unit}</span>}
    </label>
  );
}

/** A mouse or trackpad (a computer till), as opposed to a touch screen. */
const finePointer = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;
